import { Tabs, Card, Table, Button, Modal, message, Tag, Tooltip, Select, Empty, Spin, Pagination } from "antd";
import { useIsCompact } from "../../../hooks/useIsCompact";
import { AppSettingsForm, AdminFileManager } from "@components";
import type { AppSettingsFormHandle } from "Components/Setting/App/AppSettingsForm";
import ErrorLogs from "Components/Setting/ErrorLog/ErrorLog";
import CourseTemplatesSettings from "Pages/General/Settings/CourseTemplatesSettings";
import ManagerSettings from "Pages/General/Settings/ManagerSettings";
import { Api, isAdmin } from "@model";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  ExclamationCircleOutlined,
  CloudUploadOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

interface BackupRecord {
  filename: string;
  date: string;
  isSuccess: boolean;
  error?: string;
  uploadedToBackupServer: boolean;
}

const VALID_SETTINGS_TABS = new Set([
  "app",
  "errors",
  "files",
  "backups",
  "course-templates",
  "managers",
]);

export default function SettingPage() {
  const isCompact = useIsCompact();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const appSettingsFormRef = useRef<AppSettingsFormHandle>(null);
  const [savingAllSettings, setSavingAllSettings] = useState(false);
  const canManageManagers = isAdmin();

  const activeKey = useMemo(() => {
    const t = new URLSearchParams(location.search).get("tab") ?? "";
    return VALID_SETTINGS_TABS.has(t) && (t !== "managers" || canManageManagers)
      ? t
      : "app";
  }, [location.search, canManageManagers]);

  const handleTabChange = (key: string) => {
    if (key === "app") {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: key }, { replace: true });
    }
  };

  const handleSaveAllSettings = async () => {
    if (!appSettingsFormRef.current) return;

    setSavingAllSettings(true);
    try {
      await appSettingsFormRef.current.saveAll();
    } finally {
      setSavingAllSettings(false);
    }
  };

  const [backups, setBackups] = useState<BackupRecord[]>([]);
  const [backupsLoading, setBackupsLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [uploadingFilename, setUploadingFilename] = useState<string | null>(
    null,
  );
  const [backupPage, setBackupPage] = useState(1);

  const fetchBackups = useCallback(() => {
    setBackupsLoading(true);
    Api.get("/api/admin/panel/backups")
      .then((res) => {
        setBackups(res.data?.data ?? []);
      })
      .catch(() => message.error("Failed to load backup history"))
      .finally(() => setBackupsLoading(false));
  }, []);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  const handleTakeBackup = () => {
    Modal.confirm({
      title: "Take backup",
      icon: <ExclamationCircleOutlined />,
      content: "Create a new database backup now? This may take a few minutes.",
      okText: "Create backup",
      cancelText: "Cancel",
      onOk: async () => {
        setCreateLoading(true);
        try {
          const res = await Api.post("/api/admin/panel/backups");
          const data = res.data?.data;
          if (data?.isSuccess) {
            message.success("Backup created successfully");
          } else {
            message.error(data?.error || "Backup failed");
          }
          fetchBackups();
        } catch {
          message.error("Failed to create backup");
        } finally {
          setCreateLoading(false);
        }
      },
    });
  };

  const handleUploadToOneDrive = (record: BackupRecord) => {
    if (record.uploadedToBackupServer) return;
    Modal.confirm({
      title: "Upload to OneDrive",
      icon: <ExclamationCircleOutlined />,
      content: `Upload "${record.filename}" to OneDrive backup server?`,
      okText: "Upload",
      cancelText: "Cancel",
      onOk: async () => {
        setUploadingFilename(record.filename);
        try {
          const res = await Api.post(
            "/api/admin/panel/backups/upload-to-onedrive",
            {
              filename: record.filename,
            },
          );
          const data = res.data?.data;
          if (data?.ok) {
            message.success("Uploaded to OneDrive");
            fetchBackups();
          } else {
            message.error(data?.error || "Upload failed");
          }
        } catch {
          message.error("Upload request failed");
        } finally {
          setUploadingFilename(null);
        }
      },
    });
  };

  const tabOptions = [
    { value: "app", label: "App Settings" },
    { value: "errors", label: "Error Logs" },
    { value: "files", label: "File Manager" },
    { value: "course-templates", label: "Course templates" },
    { value: "backups", label: "Backups" },
    ...(canManageManagers ? [{ value: "managers", label: "Managers" }] : []),
  ];

  return (
    <>
      <Card>
        <Tabs
          activeKey={activeKey}
          onChange={handleTabChange}
          destroyInactiveTabPane
          renderTabBar={
            isCompact
              ? () => (
                  <Select
                    value={activeKey}
                    onChange={handleTabChange}
                    options={tabOptions}
                    style={{ width: "100%", marginBottom: 16 }}
                  />
                )
              : undefined
          }
        >
          <Tabs.TabPane key="app" tab="App Settings">
            <div style={{ display: "flex", justifyContent: isCompact ? "stretch" : "flex-end", marginBottom: 16 }}>
              <Button
                type="primary"
                block={isCompact}
                onClick={handleSaveAllSettings}
                loading={savingAllSettings}
              >
                Save all settings
              </Button>
            </div>
            <AppSettingsForm ref={appSettingsFormRef} />
          </Tabs.TabPane>
          <Tabs.TabPane key="errors" tab="Error Logs">
            <ErrorLogs />
          </Tabs.TabPane>
          <Tabs.TabPane key="files" tab="File Manager">
            {activeKey === "files" && <AdminFileManager />}
          </Tabs.TabPane>

          <Tabs.TabPane key="course-templates" tab="Course templates">
            {activeKey === "course-templates" && <CourseTemplatesSettings />}
          </Tabs.TabPane>

          <Tabs.TabPane key="backups" tab="Backups">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                block={isCompact}
                onClick={handleTakeBackup}
                loading={createLoading}
              >
                Take backup
              </Button>
            </div>
            {isCompact ? (
              <Spin spinning={backupsLoading}>
                {backups.length === 0 && !backupsLoading ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No backups" />
                ) : (
                  backups
                    .slice((backupPage - 1) * 10, backupPage * 10)
                    .map((record) => (
                      <div
                        key={`${record.filename}-${record.date}`}
                        style={{
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          padding: 12,
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ fontWeight: 600, wordBreak: "break-all" }}>
                          {record.filename}
                        </div>
                        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                          {record.date ? new Date(record.date).toLocaleString() : "-"}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            marginTop: 8,
                          }}
                        >
                          <Tag color={record.isSuccess ? "success" : "error"}>
                            {record.isSuccess ? "Success" : "Failed"}
                          </Tag>
                          {record.uploadedToBackupServer ? (
                            <Tooltip title="Uploaded to OneDrive">
                              <CheckCircleOutlined style={{ color: "#52c41a" }} />
                            </Tooltip>
                          ) : (
                            <Button
                              type="link"
                              size="small"
                              icon={<CloudUploadOutlined />}
                              loading={uploadingFilename === record.filename}
                              onClick={() => handleUploadToOneDrive(record)}
                              disabled={!record.isSuccess}
                            >
                              OneDrive
                            </Button>
                          )}
                        </div>
                        {record.error && (
                          <div style={{ color: "#cf1322", marginTop: 8, wordBreak: "break-word" }}>
                            {record.error}
                          </div>
                        )}
                      </div>
                    ))
                )}
                {backups.length > 10 && (
                  <Pagination
                    style={{ marginTop: 8, textAlign: "center" }}
                    size="small"
                    simple
                    current={backupPage}
                    pageSize={10}
                    total={backups.length}
                    onChange={setBackupPage}
                  />
                )}
              </Spin>
            ) : (
            <Table
              loading={backupsLoading}
              size="small"
              rowKey={(r) => `${r.filename}-${r.date}`}
              dataSource={backups}
              columns={[
                {
                  title: "Filename",
                  dataIndex: "filename",
                  key: "filename",
                },
                {
                  title: "Date",
                  dataIndex: "date",
                  key: "date",
                  width: 180,
                  render: (d: string) =>
                    d ? new Date(d).toLocaleString() : "-",
                },
                {
                  title: "Success",
                  dataIndex: "isSuccess",
                  key: "isSuccess",
                  width: 100,
                  render: (ok: boolean) => (
                    <Tag color={ok ? "success" : "error"}>
                      {ok ? "Yes" : "No"}
                    </Tag>
                  ),
                },
                {
                  title: "Error",
                  dataIndex: "error",
                  key: "error",
                  ellipsis: true,
                  render: (err: string) =>
                    err ? (
                      <Tooltip title={err}>
                        <span style={{ color: "#cf1322" }}>{err}</span>
                      </Tooltip>
                    ) : (
                      "-"
                    ),
                },
                {
                  title: "OneDrive",
                  key: "onedrive",
                  width: 100,
                  render: (_: unknown, record: BackupRecord) =>
                    record.uploadedToBackupServer ? (
                      <Tooltip title="Uploaded to OneDrive">
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Upload to OneDrive">
                        <Button
                          type="link"
                          size="small"
                          icon={<CloudUploadOutlined />}
                          loading={uploadingFilename === record.filename}
                          onClick={() => handleUploadToOneDrive(record)}
                          disabled={!record.isSuccess}
                        />
                      </Tooltip>
                ),
              },
            ]}
            />
            )}
          </Tabs.TabPane>
          {canManageManagers && (
            <Tabs.TabPane key="managers" tab="Managers">
              {activeKey === "managers" && <ManagerSettings />}
            </Tabs.TabPane>
          )}
        </Tabs>
        <br />
      </Card>
    </>
  );
}
